import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../react-core/Html/SelectEditor";
import { isNullOrEmpty } from "../../../../../../react-core/Utils/Arrays";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PaymentMethod } from "../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../Shared/Typography/Typography";
import { PaymentMethodArraysMap } from "../../Common/Resource/Contents";
import {
    getPaymentTypeOptions,
    getSourceFromOptions,
    getSourceFromString,
    getString,
    IncomeArrangementType
} from "../Resource/Content";
import { CalculateEstimatedPaye } from "./Shared/CalculateEstimatedPaye";
import { OneOffIncomeTable } from "./Shared/OneOffIncomeTable";
import { UserAssetsAllocationData, UserAssetsAllocationTable } from "./Shared/UserAssetsAllocationTable";
import { DrawdownPaymentSourceAllocation } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentSourceAllocation";
import { OneOffIncomePaymentModel } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/OneOffIncomePaymentModel";
import { CappedPensionArrangementDetail } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/CappedPensionArrangementDetail";
import { FlexiPensionArrangementDetail } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/FlexiPensionArrangementDetail";
import { toDp } from "../../../../../../react-core/Utils/Math";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import BankDetails from "./Shared/BankDetails";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { DrawdownSinglePayment } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownSinglePayment";
import { DrawdownSinglePaymentInstruction } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownSinglePaymentInstruction";
import { InvestmentAllocation } from "../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { RestrictionStore } from "../../Common/Restrictions/Variable";
import { User } from "../../../../../../Wrap/Models/Models/User/User";
import { UserStore } from "../../../../Stroes/Variable";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../Shared/Common/UserMessages";
import { PartLoading } from "../../../../../../react-core/Html/PartLoading";
import { PensionContribution } from "../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { PensionEstimatedPayQuery } from "../../../../../../Wrap/Models/Domains/Tax/PensionEstimatedPayQuery";
import { PensionIncomeConfiguration } from "../../../../../../Wrap/Constants/Enums/Domain/PensionIncomeConfiguration";
import { getQueryParameters } from "../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { DrawdownCalculationAction } from "../../Common/SIPP/DrawdownDetails/Action/DrawdownCalculationAction";
import { IncomePaymentDestination } from "../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { IncomePaymentOneOffPaymentType } from "../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentOneOffPaymentType";
import { IncomePaymentSourceFrom } from "../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentSourceFrom";
import { AllowedPaymentMethodAction } from "../../Common/Pension/PensionPayments/Actions/AllowedPaymentMethodAction";
import { PaymentSubType } from "../../../../../../Wrap/Constants/Enums/Pension/Payments/PaymentSubType";
import { AllowedPaymentMethodModel } from "../../../../../../Wrap/Models/Models/Pension/Payments/AllowedPaymentMethodModel";
import { ExcludeNonSelectiveMethod, GetAllowedMethods, GetDefaultMethod } from "../../Common/Pension/PensionPayments/Helpers/AllowedPaymentMethodHelpers";
import { CountryCode } from "../../../../../../Wrap/Constants/Enums/Domain/CountryCode";


interface IIncomePaymentOneOffProps extends IProcessComponentProps {
    customerId?: number;
    extraInfo?: any;
    isLoading: boolean;
}

interface IIncomePaymentOneOffPropsState {
    userInfo: User;
    nextPaymentDate: Date;
    allowedPaymentMethods?: AllowedPaymentMethodModel[];
}

export class IncomePaymentOneOffDetail extends ProcessComponent<
    IIncomePaymentOneOffProps,
    IIncomePaymentOneOffPropsState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
            nextPaymentDate: new Date(),
            allowedPaymentMethods: [],
        };

        this.bindFunctions();
    }

    componentDidMount() {
        super.componentDidMount();
        
        let paymentType = this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType;

        if (![IncomePaymentOneOffPaymentType.AvailableCash, IncomePaymentOneOffPaymentType.AssetsAndAvailableCash].includes(paymentType)) {
            paymentType = IncomePaymentOneOffPaymentType.AvailableCash;
            this.onPaymentTypeChange(paymentType);
        }
        this.getAllAllowedPaymentMethods();
        this.updateCashAllocation(paymentType); 
    }

    private getAllAllowedPaymentMethods(): void {
        AllowedPaymentMethodAction.GetAllAllowedPaymentMethods(getQueryParameters()["subaccounthierarchyid"], PaymentSubType.OneOffIncomePayments, undefined, (allowedPaymentMethods) => {
            this.setState({ allowedPaymentMethods: ExcludeNonSelectiveMethod(allowedPaymentMethods) });
            this.checkPaymentMethodOnConditionChange();
        });
    }

    private updateCashAllocation(paymentType: IncomePaymentOneOffPaymentType) {
        const model = this.getModel();
        if (
            paymentType === IncomePaymentOneOffPaymentType.AvailableCash &&
            model.CashAvailable?.Amount > 0 &&
            model.CashInstrument?.ProductId
        ) {
            const cashAllocation: InvestmentAllocation[] = [{
                ProductId: model.CashInstrument.ProductId,
                Percentage: 100
            }];

            const instruction = this.getModel()?.Instruction ?? {};
            instruction.DisinvestmentAllocations = cashAllocation;
            this.onDataChange(this.onDrawdownSinglePaymentInstructionChange, cashAllocation, instruction, "DisinvestmentAllocations");
        }
    }

    private bindFunctions() {
        this.getModel = this.getModel.bind(this);
        this.getPayment = this.getPayment.bind(this);
        this.onContributionChange = this.onContributionChange.bind(this);
        this.onOneOffIncomePaymentModelChange = this.onOneOffIncomePaymentModelChange.bind(this);
        this.onInstructionChange = this.onInstructionChange.bind(this);
        this.onArrangementSelectionChange = this.onArrangementSelectionChange.bind(this);
        this.onDrawdownPaymentDestinationAllocationChange = this.onDrawdownPaymentDestinationAllocationChange.bind(
            this
        );
        this.onPaymentChange = this.onPaymentChange.bind(this);
        this.onDrawdownSinglePaymentInstructionChange = this.onDrawdownSinglePaymentInstructionChange.bind(this);
        this.onPaymentAmountChange = this.onPaymentAmountChange.bind(this);
        this.onPaymentTargetSpecificArrangementsChange = this.onPaymentTargetSpecificArrangementsChange.bind(this);
        this.onPaymentMethodChange = this.onPaymentMethodChange.bind(this);
        this.onDisinvestmentAllocationsChange = this.onDisinvestmentAllocationsChange.bind(this);
        this.checkPaymentMethodOnConditionChange = this.checkPaymentMethodOnConditionChange.bind(this);
    }

    public isValid(): boolean {

        let isCashOrAssetSufficient = true;
        const payment = this.getModel()?.Instruction?.Payment;

        if (payment.TargetSpecificArrangements) {
            const total = this.getTargetTotal();

            if (total == 0) {
                this.props.showErrorMsgPopUp(["Please enter income amount"], true);
                return false;
            }

            if (payment?.IncomePaymentOneOffPaymentType === IncomePaymentOneOffPaymentType.AvailableCash) {
                const cashAvailible = this.getModel()?.CashAvailable?.Amount ?? 0;
                if (total > cashAvailible) {
                    isCashOrAssetSufficient = false;
                }
            }

            if (payment?.IncomePaymentOneOffPaymentType === IncomePaymentOneOffPaymentType.AssetsAndAvailableCash) {
                const totalEstimatedValueAvailable = this.getModel()?.TotalEstimatedValueAvailable?.Amount ?? 0;
                if (total > totalEstimatedValueAvailable) {
                    isCashOrAssetSufficient = false;
                }
            }
            if (!isCashOrAssetSufficient) {
                this.props.showErrorMsgPopUp(["Current value of Cash and/or Assets are not sufficient to satisfy the total amount(s) entered in One off Income payment field(s)"], true);
                return false
            }

            
        }

        return super.isValid() && !RestrictionStore.WithdrawDisabled();
    }

    private getTargetTotal(): number {
        return (this.getModel().Instruction?.Payment?.PaymentSourceAllocations ?? []).reduce((total, ele) => {
            return total + ele?.Amount?.Amount;
        }, 0);
    }

    private onContributionChange(newData: PensionContribution): void {
        const { onChange, data } = this.props;
        this.onDataChange(onChange, newData, data, "Contribution");
    }

    private onOneOffIncomePaymentModelChange(newData: OneOffIncomePaymentModel): void {
        const contribution = this.props.data?.Contribution;
        this.onDataChange(this.onContributionChange, newData, contribution, "OneOffIncomePaymentModel");
    }

    private onInstructionChange(newData: DrawdownSinglePaymentInstruction): void {
        const oneOffIncomePaymentModel = this.props.data?.Contribution?.OneOffIncomePaymentModel;
        this.onDataChange(this.onOneOffIncomePaymentModelChange, newData, oneOffIncomePaymentModel, "Instruction");
    }

    private onArrangementSelectionChange(newAllocations: DrawdownPaymentSourceAllocation[]): void {
        const payment = this.getModel()?.Instruction?.Payment;
        if (payment == null || isNullOrEmpty(payment?.PaymentSourceAllocations)) {
            this.onPaymentChange({ PaymentSourceAllocations: newAllocations });
            return;
        }
        this.onDataChange(this.onPaymentChange, newAllocations, payment, "PaymentSourceAllocations");
    }

    private onDrawdownPaymentDestinationAllocationChange(newAllocations: DrawdownPaymentDestinationAllocation[]): void {
        const payment = this.getModel()?.Instruction?.Payment;
        if (payment == null || isNullOrEmpty(payment?.PaymentDestinationAllocations)) {
            this.onPaymentChange({ PaymentDestinationAllocations: newAllocations });
            this.checkPaymentMethodOnConditionChange(newAllocations);
            return;
        }
        this.onDataChange(this.onPaymentChange, newAllocations, payment, "PaymentDestinationAllocations");

        this.checkPaymentMethodOnConditionChange();
    }

    private onPaymentChange(newData: DrawdownSinglePayment): void {
        const instruction = this.getModel()?.Instruction;
        this.onDataChange(this.onDrawdownSinglePaymentInstructionChange, newData, instruction, "Payment");
    }

    private onDrawdownSinglePaymentInstructionChange(newData: DrawdownSinglePaymentInstruction): void {
        const instruction = this.getModel()?.Instruction;
        this.onDataChange(this.onInstructionChange, newData, instruction);
    }

    private onPaymentAmountChange(newData: number): void {
        var payment = this.getModel()?.Instruction?.Payment;
        const hasCapped = this.getModel()?.CappedPensionArrangements?.length > 0;
        const data = this.getModel();

        const maximumPaymentsAvailable =
            data.Instruction?.Payment?.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AvailableCash
                ?
                data.CashAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                    ? data.CashAvailable.Amount
                    : data.StandardAlgorithmAvailable.Amount

                :
                data.TotalEstimatedValueAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                    ? data.TotalEstimatedValueAvailable.Amount
                    : data.StandardAlgorithmAvailable.Amount

        const fullAmount = (hasCapped == false && newData == maximumPaymentsAvailable) ? true : false;

        if (payment == null) {
            this.onPaymentChange({
                Amount: { Amount: newData, Currency: CurrencyCode.GBP },
                PaymentSourceAllocations: [],
                PaymentDestinationAllocations: [],
                FullAmount: fullAmount
            });
            return;
        }

        let newPaymentAmount: Money;
        if (payment.Amount) {
            newPaymentAmount = { Amount: newData, Currency: payment.Amount.Currency };
        } else {
            newPaymentAmount = { Amount: newData, Currency: CurrencyCode.GBP };
        }
        payment.Amount = newPaymentAmount;
        payment.FullAmount = fullAmount;
        this.onPaymentChange(payment);      
        this.checkPaymentMethodOnConditionChange();
    }

    private checkPaymentMethodOnConditionChange(newAllocations: DrawdownPaymentDestinationAllocation[] = []): void {
        const paymentAmount = this.getPayment()?.Amount?.Amount;
        const currentMethod = this.getPaymentMethod() || PaymentMethod.NotSpecified;
        var paymentDestinationAllocations = newAllocations.length > 0 ? newAllocations : this.getPayment()?.PaymentDestinationAllocations;
        var selectedBankAccount = paymentDestinationAllocations?.filter(alloc => alloc?.Destination == IncomePaymentDestination.BankAccount)?.[0];
        var allBankAccounts = this.props.extraInfo?.AllBankAccounts ?? [];
        if(selectedBankAccount === undefined && currentMethod !== PaymentMethod.InternalTransfer){
            this.onPaymentMethodChange(PaymentMethod.InternalTransfer);
        }else if(selectedBankAccount !== undefined){
            const bankAccount = allBankAccounts.find((ba) => ba.BankAccountId === selectedBankAccount.BankAccountId);
            if (bankAccount && bankAccount.BankCountry !== CountryCode.GBR) {
                this.onPaymentMethodChange(PaymentMethod.Swift);
            }else{
                const allowedMethods = GetAllowedMethods(this.state.allowedPaymentMethods, paymentAmount, false);
                if(!allowedMethods.includes(currentMethod)){
                    const defaultMethod = GetDefaultMethod(this.state.allowedPaymentMethods, paymentAmount);
                    this.onPaymentMethodChange(defaultMethod);
                }
            }
        }
    }

    private onPaymentMethodChange(newPaymentMethod: PaymentMethod): void {
        const payment = this.getModel()?.Instruction?.Payment;
        if (payment == null) {
            this.onPaymentChange({
                PaymentMethod: newPaymentMethod
            });
            return;
        }

        payment.PaymentMethod = newPaymentMethod;

        this.onPaymentChange(payment);
        this.setNextPaymentDate(newPaymentMethod);
    }

    private onPaymentTypeChange(paymentType: IncomePaymentOneOffPaymentType): void {
        const payment = this.getModel()?.Instruction?.Payment;

        if (payment == null) {
            if (paymentType == IncomePaymentOneOffPaymentType.AssetsAndAvailableCash) {
                this.onPaymentChange({
                    IncomePaymentOneOffPaymentType: paymentType,
                    IncomePaymentSourceFrom: IncomePaymentSourceFrom.SpecifyAmount
                });
            } else {
                this.onPaymentChange({
                    IncomePaymentOneOffPaymentType: paymentType
                });
            }

            return;
        }

        payment.IncomePaymentOneOffPaymentType = paymentType;
        if (paymentType == IncomePaymentOneOffPaymentType.AssetsAndAvailableCash && !payment?.IncomePaymentSourceFrom || payment.IncomePaymentSourceFrom == IncomePaymentSourceFrom.NotSpecified) {
            payment.IncomePaymentSourceFrom = IncomePaymentSourceFrom.SpecifyAmount
        }

        this.onPaymentChange(payment);
    }

    private onPaymentSourceChange(paymentSource: IncomePaymentSourceFrom): void {
        const payment = this.getModel()?.Instruction?.Payment;

        if (payment == null) {
            this.onPaymentChange({
                IncomePaymentSourceFrom: paymentSource
            });
            return;
        }

        payment.IncomePaymentSourceFrom = paymentSource;

        this.onPaymentChange(payment);
    }

    private onPaymentTargetSpecificArrangementsChange(targetSpecificArrangements: boolean): void {
        const payment = this.getModel()?.Instruction?.Payment;
        if (payment == null) {
            this.onPaymentChange({ TargetSpecificArrangements: targetSpecificArrangements });
            return;
        }
        payment.TargetSpecificArrangements = targetSpecificArrangements
        this.onPaymentChange(payment);
    }

    private onDisinvestmentAllocationsChange(newAllocation: Money, productId: number): void {
        const instruction = this.getModel()?.Instruction ?? {};
        let disinvestmentAllocations = instruction.DisinvestmentAllocations ?? [];

        disinvestmentAllocations = disinvestmentAllocations.filter((x) => x.ProductId !== productId);
        const allocation: InvestmentAllocation = { ProductId: productId, Value: newAllocation };
        disinvestmentAllocations.push(allocation);

        instruction.DisinvestmentAllocations = disinvestmentAllocations;

        this.onDataChange(this.onDrawdownSinglePaymentInstructionChange, disinvestmentAllocations, instruction, "DisinvestmentAllocations");
    }

    private calculateTotalValue(): number {
        const disinvestmentAllocations = this.getModel()?.Instruction?.DisinvestmentAllocations ?? [];
        return disinvestmentAllocations.reduce((sum, allocation) => sum + allocation.Value.Amount, 0);
    }


    public hasInheritedDrawdowns(): boolean {
        const flexi = this.getFlexiList().some((a) => a?.PensionArrangementDetail?.IsInherited);
        const capped = this.getCappedList().some((a) => a?.PensionArrangementDetail?.IsInherited);
        return flexi || capped;
    }

    private getPayment(): DrawdownSinglePayment {
        return this.getModel().Instruction?.Payment;
    }

    private getPaymentMethod(): PaymentMethod {
        return this.getModel().Instruction?.Payment?.PaymentMethod;
    }

    private getFlexiList(): FlexiPensionArrangementDetail[] {
        return this.getModel().FlexiPensionArrangements ?? [];
    }

    private getCappedList(): CappedPensionArrangementDetail[] {
        return this.getModel().CappedPensionArrangements ?? [];
    }

    private getArrangementSelection(): DrawdownPaymentSourceAllocation[] {
        return this.getModel().Instruction?.Payment?.PaymentSourceAllocations ?? [];
    }

    private getModel(): OneOffIncomePaymentModel {
        return this.props.data?.Contribution?.OneOffIncomePaymentModel ?? {};
    }

    private getDataForAssetsAllocationTable(): UserAssetsAllocationData[] {
        const model = this.getModel();
        const accountHoldings = model.Holdings ?? [];
        const allocations = accountHoldings.map((h) => {
            const availableValue = toDp((h.MarketValue.Amount / h.Quantity) * h.AvailableQuantity, 2);
            const availableMarketValue: Money = { Amount: availableValue, Currency: h.MarketValue.Currency };

            return {
                AssetId: h.ProductId,
                Investment: h.ProductCode,
                AvailableMarketValue: availableMarketValue,
                Allocation: null,
                SecurityType: h.SecurityType
            };
        });
        const disinvestmentAllocations = Array.isArray(model.Instruction?.DisinvestmentAllocations)
            ? model.Instruction?.DisinvestmentAllocations
            : [];

        if (isNullOrEmpty(disinvestmentAllocations)) {
            return allocations;
        }

        const disinvestmentDict = {};
        disinvestmentAllocations.forEach((x) => (disinvestmentDict[x.ProductId] = x));

        allocations.forEach((a) => {
            const disinvestment = disinvestmentDict[a.AssetId];
            if (disinvestment) {
                a.Allocation = disinvestment.Value;
            }
        });

        return allocations;
    }

    private clientDetailRender() {
        const data = this.getModel();

        const config = [
            {
                title: "Total estimated value available",
                data: MoneyDisplayHelper.getMoneyFormat(
                    data?.TotalEstimatedValueAvailable ?? { Amount: 0, Currency: CurrencyCode.GBP }
                ),
            },
            {
                title: "Which is made up of:",
                data: " ",
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat(
                    data?.CashAvailable ?? { Amount: 0, Currency: CurrencyCode.GBP }
                ),
            },
            {
                title: "Estimated asset value available",
                data: MoneyDisplayHelper.getMoneyFormat(
                    data?.EstimatedAssetValueAvailable ?? { Amount: 0, Currency: CurrencyCode.GBP }
                ),
            },
        ];
        return (
            <div>
                <h2 className="mb-30">Income payment details</h2>
                <h3>Available single income payment</h3>
                <Typography type="common" config={config}></Typography>
            </div>
        );
    }

    private setNextPaymentDate(paymentMethod: PaymentMethod) {
        DrawdownCalculationAction.getMinimumPaymentDate(
            getQueryParameters()["subaccounthierarchyid"],
            false,
            false,
            (result) => {
                this.setState({
                    nextPaymentDate: new Date(result.MinimumDateTime),
                });
            },
            PaymentMethodArraysMap[paymentMethod],
        );
    }
    private getCalculateEstimatedPayeQueries(): Array<PensionEstimatedPayQuery> {

        if (this.getPayment()?.TargetSpecificArrangements) {
            var queryList: Array<PensionEstimatedPayQuery> = [];
            var allocations = this.getPayment()?.PaymentSourceAllocations ?? [];
            allocations.forEach(allocation => {
                if (allocation?.Amount?.Amount > 0) {

                    queryList.push(
                        {
                            HierarchyId: getQueryParameters()["subaccounthierarchyid"],
                            PensionArrangementId: allocation.ArrangementId,
                            PensionIncomeConfiguration: PensionIncomeConfiguration.ArrangementLevel,
                            GrossAmount: allocation?.Amount,
                            Frequency: Frequency.Monthly,
                            PaymentDate: JsonDateParser.toJsonDateNoOffset(this.state.nextPaymentDate),
                        }
                    )
                }
            })
            return queryList;
        }
        else {

            return [
                {
                    HierarchyId: getQueryParameters()["subaccounthierarchyid"],
                    PensionIncomeConfiguration: PensionIncomeConfiguration.PensionLevel,
                    GrossAmount: this.getPayment()?.Amount,
                    Frequency: Frequency.Monthly,
                    PaymentDate: JsonDateParser.toJsonDateNoOffset(this.state.nextPaymentDate),
                }]
        }
    }

    render() {
        const { extraInfo, onChange } = this.props;
        const data = this.getModel();
        if (this.props.isLoading) {
            return <PartLoading isLoading={true}></PartLoading>;
        }

        let restrictedMessage = "An account with suspended or restricted activities cannot setup withdrawals";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        const bankAccountDestinations = data?.Instruction?.Payment?.PaymentDestinationAllocations?.filter(alloc => alloc.Destination == IncomePaymentDestination.BankAccount).map(x => x.BankAccountId) ?? [];
        const hasInternationalBankAccountDestination = bankAccountDestinations?.some(x => {
            var allBankAccounts = this.props.extraInfo?.AllBankAccounts ?? [];
            const bankAccount = allBankAccounts.find((ba) => ba.BankAccountId === x);
            return bankAccount && bankAccount.BankCountry !== CountryCode.GBR;
        })
        return (
            <>
                {RestrictionStore.WithdrawDisabled() && (
                    <Alert alertType={AlertType.Error} message={restrictedMessage} />
                )}
                {this.clientDetailRender()}
                <hr className="mb-30" />

                <h3>Set single income payment details</h3>
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Do you wish to target specific arrangements?"}
                    tooltip="Where a customer has more than one drawdown arrangement, you are able to choose which arrangements are used to provide income withdrawals. Selecting specific arrangements can be beneficial for clients who have inherited drawdown benefits which have different tax treatments. Or you can select 'No' and the platform will automatically choose the arrangements to be used."
                    selected={this.getPayment()?.TargetSpecificArrangements ?? false}
                    disabled={false}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => {
                        this.onPaymentTargetSpecificArrangementsChange(v);
                    }}
                    required={"Please select "}
                />
                {this.hasInheritedDrawdowns() && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            " SL to provide content around whether the inherited drawdowns should be targeted or excluded and they should consider the use of the standard algorithm or targeting arrangements"
                        }
                    />
                )}

                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label="Payment type"
                    options={getPaymentTypeOptions()}
                    getText={(option) => getString(option)}
                    onChange={(v) => {
                        const instruction = this.getModel()?.Instruction ?? {};
                        this.onPaymentTypeChange(v)
                        if (v === IncomePaymentOneOffPaymentType.AvailableCash) {
                            this.updateCashAllocation(v);
                        } else {
                            instruction.DisinvestmentAllocations = [];
                            this.onDataChange(this.onDrawdownSinglePaymentInstructionChange, [], instruction, "DisinvestmentAllocations");
                        }
                    }}
                    selected={this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType ?? IncomePaymentOneOffPaymentType.AvailableCash}
                    required={"Please select Payment Type"}
                />

                {!this.getPayment()?.TargetSpecificArrangements && <div className="row">
                    <div className="col-12 col-sm-auto">
                        <p>Maximum estimated income payment available from the pension</p>
                    </div>
                    <div className="col-12 col-sm-fluid">
                        <span>
                            {this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AvailableCash
                                ? MoneyDisplayHelper.getMoneyFormat(
                                    data.CashAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                                        ? data.CashAvailable
                                        : data.StandardAlgorithmAvailable
                                )
                                : MoneyDisplayHelper.getMoneyFormat(
                                    data.TotalEstimatedValueAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                                        ? data.TotalEstimatedValueAvailable
                                        : data.StandardAlgorithmAvailable
                                )}
                        </span>
                    </div>
                </div>}
                {this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AssetsAndAvailableCash && <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label="Payment amount"
                    options={getSourceFromOptions()}
                    getText={(option) => getSourceFromString(option)}
                    onChange={(v) => {
                        this.onPaymentSourceChange(v)
                    }}
                    alwaysValidateOnUpdate={false}
                    selected={this.getModel()?.Instruction?.Payment?.IncomePaymentSourceFrom ?? IncomePaymentSourceFrom.SpecifyAmount}
                    required={"Please select a payment amount."}
                />}
                {!this.getPayment()?.TargetSpecificArrangements
                    && (this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AvailableCash || this.getModel()?.Instruction?.Payment?.IncomePaymentSourceFrom == IncomePaymentSourceFrom.SpecifyAmount)
                    && <div className="row">
                    <div className="col-12 col-md-4">
                        <MoneyTextBoxEditor
                            required={"Please enter payment amount"}
                            value={this.getPayment()?.Amount?.Amount ?? 0}
                            currency={this.getPayment()?.Amount?.Currency ?? CurrencyCode.GBP}
                            label={""}
                            alwaysValidateOnUpdate={false}
                            onChange={(v) => {
                                this.onPaymentAmountChange(v);
                                const allocation = { Amount: v, Currency: this.getPayment()?.Amount?.Currency ?? CurrencyCode.GBP };
                                this.onDisinvestmentAllocationsChange(allocation, data.CashInstrument.ProductId);
                            }}
                            validationRules={[
                                {
                                    errorMessage: "Value must be greater than 0.",
                                    validator: (v) => v > 0,
                                },
                                {
                                    errorMessage:
                                        "value can't be greater than maximum estimated income payment available from the pension",
                                    validator: (v) => {
                                        if (
                                            this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType ==
                                            IncomePaymentOneOffPaymentType.AvailableCash
                                        ) {
                                            return v <= (data.CashAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                                                ? data.CashAvailable.Amount
                                                : data.StandardAlgorithmAvailable.Amount);
                                        } else {
                                            return v <= (data.TotalEstimatedValueAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                                                ? data.TotalEstimatedValueAvailable.Amount
                                                : data.StandardAlgorithmAvailable.Amount);
                                        }
                                    },
                                },
                                {
                                    errorMessage:
                                        "Amount exceeds the availible cash, please choose 'Specify asset/cash'",
                                    validator: (v) => {
                                        if (this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AssetsAndAvailableCash
                                            && this.getModel()?.Instruction?.Payment?.IncomePaymentSourceFrom == IncomePaymentSourceFrom.SpecifyAmount)
                                        {
                                            return v <= (data.CashAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                                                ? data.CashAvailable.Amount
                                                : data.StandardAlgorithmAvailable.Amount);
                                        }
                                        return true;
                                    },
                                }
                            ]}
                        />
                    </div>
                </div>}
                {this.getModel()?.Instruction?.Payment?.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AssetsAndAvailableCash && this.getModel()?.Instruction?.Payment?.IncomePaymentSourceFrom == IncomePaymentSourceFrom.SpecifyAssetorCash && (
                    <UserAssetsAllocationTable
                        totalValue={this.getModel()?.Instruction?.Payment?.TargetSpecificArrangements ?
                            { Amount: this.getTargetTotal(), Currency: this.getPayment().Amount?.Currency ?? CurrencyCode.GBP }
                            : this.getPayment()?.Amount}
                        maximumEstimatedAmount={data.TotalEstimatedValueAvailable.Amount < data.StandardAlgorithmAvailable.Amount
                            ? data.TotalEstimatedValueAvailable.Amount
                            : data.StandardAlgorithmAvailable.Amount}
                        data={this.getDataForAssetsAllocationTable()}
                        onChange={(newAllocation, productId) => {
                            this.onDisinvestmentAllocationsChange(newAllocation, productId);

                            const updatedTotalValue = this.calculateTotalValue();
                            this.onPaymentAmountChange(updatedTotalValue);
                        }}
                        onRef={(ref) => this.appendChild(ref)}
                        showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                    />
                )}

                {this.getModel()?.Instruction?.Payment?.TargetSpecificArrangements && this.getCappedList().length > 0 && (
                    <OneOffIncomeTable
                        cssWrapper=""
                        tableTitle="Capped"
                        ArrangementType={IncomeArrangementType.Capped}
                        data={this.getCappedList()}
                        arrangementSelection={this.getArrangementSelection()}
                        onChange={this.onArrangementSelectionChange}
                        onRef={(ref) => this.appendChild(ref)}
                    />
                )}

                {this.getModel()?.Instruction?.Payment?.TargetSpecificArrangements && this.getFlexiList().length > 0 && (
                    <OneOffIncomeTable
                        cssWrapper=""
                        tableTitle="Flexible"
                        ArrangementType={IncomeArrangementType.Flexi}
                        data={this.getFlexiList()}
                        arrangementSelection={this.getArrangementSelection()}
                        onChange={this.onArrangementSelectionChange}
                        onRef={(ref) => this.appendChild(ref)}
                    />
                )}

                {bankAccountDestinations.length > 0 && (
                    <div className="row">
                        <div className="col-12 col-md-4">
                            <SelectEditor
                                label={"Payment method"}
                                options={GetAllowedMethods(this.state.allowedPaymentMethods, this.getPayment()?.Amount?.Amount, hasInternationalBankAccountDestination)}
                                selected={this.getPaymentMethod()}
                                getText={(v) => {
                                    return PaymentMethodArraysMap[v];
                                }}
                                onChange={(v) => {
                                    this.onPaymentMethodChange(v);
                                }}
                                disabled = {hasInternationalBankAccountDestination}
                            />
                        </div>
                    </div>
                )}

                <hr className="mb-30" />
                <h3>Estimated PAYE</h3>
                <CalculateEstimatedPaye query={this.getCalculateEstimatedPayeQueries()} />
                <hr className="mb-30" />
                <h3>Account details</h3>
                {
                    <BankDetails
                        ownerHierarchyId={getQueryParameters()["hierarchyid"]}
                        allBankAccounts={extraInfo?.AllBankAccounts ?? []}
                        bankAccountInstructions={this.props.data?.BankAccountInstructions ?? []}
                        incomePaymentAllocations={data?.Instruction?.Payment?.PaymentDestinationAllocations ?? []}
                        onChange={this.onDrawdownPaymentDestinationAllocationChange}
                        onRef={(ref) => this.appendChild(ref)}
                        showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                        data={data}
                        onChangeBankAccountInstructions={(changes) => {
                            this.onDataChange(onChange, { BankAccountInstructions: changes }, data);
                        }}
                    />
                }
            </>
        );
    }
}
